import usePrefetch from '../hooks/usePrefetch';
import Breadcrumbs from './Breadcrumbs';
import { CartContext } from './context/CartContext';
import { Container } from 'react-bootstrap';
import {
    Link,
    useLocation,
    useParams,
    useSearchParams
} from 'react-router-dom';
import { LoadingContext } from './context/LoadingContext';
import ProductAddToCartForm from './ProductAddToCartForm';
import ProductGallery from './ProductGallery';
import ProductReviews from './ProductReviews';
import QuestionsAndAnswers from './QuestionsAndAnswers';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import ReportProductIssueModal from './ReportProductIssueModal';
import ShareIconList from './ShareIconList';
import '../styles/Product.css';
import SEO from './SEO';
import AppContext from './context/AppContext';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import ProductAddToCartFormSkeleton from './ProductAddToCartFormSkeleton';
import useBuildProductStructuredData from '../hooks/useBuildProductStructuredData';
import ProductOftenBoughtWithProducts from './ProductOftenBoughtWithProducts';
import PropTypes from 'prop-types';
import ProductChildProductsTable from './ProductChildProductsTable';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';
import getSymbolFromCurrency from 'currency-symbol-map';

/** CONFIGURATION */
const BASE_URL = process.env.APP_URL.replace(/\/$/, '');
export const MAX_PRODUCT_VIEWED = 25;

/** HELPERS */

export const fetchProductData = async (handle) => {
    const response = await fetch(`${BASE_URL}/api/products/handle/${handle}`);
    const data = await response.json();
    return data?.product ?? null;
};

export const getMatchingVariant = (variants, selectedOptions) => {
    return variants.filter(
        (variant) => {
            if (!variant.Options.length) {
                return false;
            }

            return variant.Options.every((option) => {
                const selectedOptionValue = selectedOptions.get(option.id);
                if (!selectedOptionValue)
                    return false;
                const { optionValueSelected } = option.VariantSelectedOptions;
                return (selectedOptionValue === optionValueSelected);
            });
        }
    ).at(0) ?? null;
};
/**
 * Component for showing the product page.
 * @component
 */
const Product = ({ isMobile }) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const { cartState } = useContext(CartContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);
    const [stateLoaded, setStateLoaded] = useState(false);
    const structuredData = useBuildProductStructuredData();

    /** Dynamic Paths */
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const productBasePath = productPath[0].pathBeforeParams;

    /** States */
    const [ breadcrumbList, setBreadcrumbList ] = useState([]);
    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ maxQuantity, setMaxQuantity ] = useState(0);
    const [ price, setPrice ] = useState(0);
    const [ selectedOptions, setSelectedOptions ] = useState(new Map());
    const [ selectedVariant, setSelectedVariant ] = useState(null);
    const [ totalCartQuantity, setTotalCartQuantity ] = useState(0);

    /** URL-related Data */
    const location = useLocation();
    const { collectionHandle, handle } = useParams();
    const [ searchParams ] = useSearchParams();

    /** Model States */


    // Pre-fetch product data.
    const { isLoaded: productLoaded, data: product, error } = usePrefetch({
        key: 'product',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchProductData(handle);
            await finishLoading();
            return data;
        },
    });


    // Load product data
    useEffect(() => {
        if (!product) {
            return;
        }
        if (error) {
            setErrorMessage(error.message);
            return;
        }
        const { Variants: variants } = product;
        if (variants.length === 1) {
            const defaultVariant = variants.at(0);
            setPrice(defaultVariant.price);
            setSelectedVariant(defaultVariant);
        } else {
            setPrice(product.price);
            setSelectedVariant(null);
        }
        setSelectedOptions(new Map());
        setStateLoaded(true);
        const {
            Collections: collections,
            title,
        } = product;
        let breadcrumbs = [[ title, location.pathname ]];
        if (collectionHandle) {
            const collection = collections.find(
                (collection) => collection.handle === collectionHandle
            );
            if (!collection) {
                setErrorMessage('Product not found in collection.');
                return;
            }
            if (collection) {
                breadcrumbs.unshift([
                    collection.title,
                    `${collectionPath[0].pathBeforeParams}/${collectionHandle}`,
                ]);
            }
        }
        setBreadcrumbList(breadcrumbs);
    }, [
        collectionHandle,
        error,
        location.pathname,
        product,
    ]);
    // Update variant from searchParams
    useEffect(() => {
        const variantId = searchParams.get('variant');
        if (product
            && variantId) {
            const { Variants: variants } = product;
            const selectedVariant = variants?.filter((v) => v.id === parseInt(variantId)).at(0) ?? null;
            setSelectedVariant(selectedVariant);
            const newSelectedOptions = new Map();
            if (selectedVariant) {
                for (const option of selectedVariant.Options) {
                    newSelectedOptions.set(option.id, option.VariantSelectedOptions.optionValueSelected);
                }
            }
            setSelectedOptions(newSelectedOptions);
            setPrice(selectedVariant.price);
        }
    }, [product, searchParams]);

    // Update total & max. qty on cart state changes
    useEffect(() => {
        let cartQty = 0;
        let maxQty = 0;
        const variant = selectedVariant ?? (product?.Variants.length === 1
            ? product?.Variants.at(0)
            : product?.Variants.find(
                (variant) => variant.title === 'Default Title'
                    || variant.variantPosition === 1
            )
        );
        if (variant) {
            maxQty = variant.availableQuantity;
            const inventoryLevels = variant.availableInventoryLevels.filter((l) => l.variantId === variant.id);
            inventoryLevels.forEach((inventoryLevel) => {
                if (inventoryLevel.bypassNegativeQuantity === true) {
                    maxQty = Infinity;
                }
                const cartItem = cartState.find((i) => i.inventoryLevelId === inventoryLevel.id);
                if (cartItem) {
                    if (maxQty !== Infinity) {
                        maxQty -= cartItem.quantity;
                    }
                    cartQty += cartItem.quantity;
                }
            });
        }
        setMaxQuantity(maxQty);
        setTotalCartQuantity(cartQty);
    }, [selectedVariant, cartState, product]);

    // Update recently viewed products in localStorage
    useEffect(() => {
        if (product) {
            // eslint-disable-next-line no-undef
            const recentlyViewed = localStorage.getItem('recentlyViewedProducts')?.split(',').filter(Boolean) ?? [];
            const newRecentlyViewed = [product.id,
                ...recentlyViewed.filter((p) => p !== product.id)]
                .slice(0, MAX_PRODUCT_VIEWED)
                .join(',');
            // eslint-disable-next-line no-undef
            localStorage.setItem('recentlyViewedProducts', newRecentlyViewed);
        }
    }, [product]);

    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!productLoaded
            || !product)
            return '';
        let title = product.seoTitle ?? product.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [productLoaded, product]);

    /* Determine whether or not all options have been selected */
    const isAllOptionsSelected = useMemo(() => {
        if (!productLoaded
            || !product
            || !selectedOptions.size)
            return false;
        const { Options: options } = product;
        return options.every((o) => selectedOptions.get(o.id) !== undefined);
    }, [productLoaded, product, selectedOptions]);

    /* Set selected option */
    const selectOption = useCallback((optionId, value) => {
        if (!productLoaded
            || !product)
            return;
        const currentValue = selectedOptions.get(optionId);
        if (!currentValue && value === '')
            return;
        const newSelectedOptions = new Map(selectedOptions);
        if (value === '') {
            newSelectedOptions.delete(optionId);
        } else {
            newSelectedOptions.set(optionId, value);
        }
        setSelectedOptions(newSelectedOptions);
        const selectedVariant = getMatchingVariant(product.Variants, newSelectedOptions);
        setSelectedVariant(selectedVariant);
        if (selectedVariant !== null) {
            setPrice(selectedVariant.price);
        }
    }, [productLoaded, product, selectedOptions, getMatchingVariant]);

    /* Render content */
    const content = () => {
        if (errorMessage) {
            return <p>{errorMessage}</p>;
        }
        if (productLoaded === false) {
            return <p>Fetching product...</p>;
        }
        if (productLoaded === true && !product) {
            return <NotFound />;
        }
        return <>
            <SEO
                seoTitle={title}
                seoDescription={product.seoDescription ?? ''}
                seoType={'product'}
                canonicalUrl={new URL(`${productBasePath}/${product.handle}`, process.env.APP_URL).toString()}
                breadsArr={[
                    {
                        handle: '/',
                        title: 'Home',
                    },
                    {
                        title: 'Products',
                        handle: `${productBasePath}/`,
                    },
                    {
                        title: product.seoTitle ?? product.title,
                        handle: `${productBasePath}/${product.handle}`,
                    },
                ]}
                image={product.imageUrl}
                richSnippet={structuredData(product)}
            />
            <Breadcrumbs
                breadcrumbList={breadcrumbList}
                loading={!stateLoaded} />
            <section
                className={`product-page--info${stateLoaded ? '' : ' loading'}`}>
                <ProductGallery
                    product={product}
                    images={product.Images}
                />
                <section
                    className={'product-page--details'}>
                    <h1
                        style={{ maxWidth: '800px' }}>{product.title}</h1>
                    {/** @todo Add vendor collections and link to collection page. */}
                    <p>
                        by <Link
                            className={'product-page--vendor-name'}
                            to={`/pages/search-results?v=${encodeURIComponent(product.vendor)}`} >
                            {product.vendor}
                        </Link>
                    </p>
                    {/** @todo Add reviews. */}
                    {/** @todo Add indicator for how much is saved when product is discounted. */}
                    {!!stateLoaded && (<p
                        className={'product-page--price-container'}>
                        Price: <span
                            className={'product-page--price-value'}>
                            ${(Math.round(price * 100) / 100).toFixed(2)} {appConfig.storeCurrency}
                        </span>
                    </p>)}
                    {
                        (
                            (
                                (selectedVariant?.availableForSale === true || selectedVariant?.availableForSale === 1)
                                && !isNaN(selectedVariant?.compareAtPrice)
                                && selectedVariant?.compareAtPrice > selectedVariant?.price
                            )
                            ||
                            (
                                (product?.availableForSale === true || product?.availableForSale === 1)
                                && !isNaN(product?.compareAtPrice)
                                && product?.compareAtPrice > product?.price
                            )
                        )
                        && (<div
                            className={'mt-3'}><b>Save</b> <span
                                className={'text-decoration-line-through'}> {getSymbolFromCurrency(appConfig.storeCurrency)}{(selectedVariant ? selectedVariant.compareAtPrice - selectedVariant.price : product.compareAtPrice - product.price).toFixed(2)} {appConfig.storeCurrency}</span></div>
                        )
                    }
                    {!stateLoaded && (<p
                        className={'product-page--price-container'}>
                    </p>)}
                    {/** @todo Update max quantity when variant is changed or added to cart. */}
                    {!!stateLoaded && (<ProductAddToCartForm
                        isProductAvailableForSale={product.availableForSale}
                        isAllOptionsSelected={isAllOptionsSelected}
                        maxQuantity={maxQuantity}
                        options={product.Options}
                        price={price}
                        selectedOptions={selectedOptions}
                        selectOption={selectOption}
                        selectedVariant={selectedVariant}
                        variants={product.Variants}
                        totalCartQuantity={totalCartQuantity}
                    />)}
                    {!stateLoaded && (<ProductAddToCartFormSkeleton />)}

                    <p><strong>Free Shipping</strong> on all orders <strong>over {appConfig.freeShippingAmount}</strong> for continental USA. No hidden fees.</p>
                    <p><strong>Shipping & Returns</strong>: <Link
                        to={'/pages/shipping-returns'}>Learn More</Link></p>
                    <div
                        className={'product-page--share-section'}>
                        <strong>Share this:</strong><br />
                        <ShareIconList
                            iconSize={'24px'} />
                    </div>
                    <ReportProductIssueModal
                        productId={product.id} />
                    <h2>Description</h2>
                    <div
                        dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}>
                    </div>
                </section>
            </section>
            <br />
            <ProductChildProductsTable
                productId={product.id} />
            <ProductReviews
                product={product}
            />
            <QuestionsAndAnswers
                productId={product.id}
            />
            <ProductOftenBoughtWithProducts
                isMobile={isMobile}
                productHandle={product.handle}
            />
        </>;
    };

    return <Container
        className={'page--section'}
        fluid>{content()}</Container>;
};

Product.propTypes = {
    isMobile: PropTypes.bool,
};

export default Product;
